import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';
import { Box } from '@radix-ui/themes';
import 'swiper/css';
import 'swiper/css/pagination';

import { useSelector } from 'react-redux';
import { selectDevice } from 'store/slices/common';

export const BannerSlider = ({ banners }) => {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  const swiperRef = useRef(null);
  const isPlayingRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(true);

  const updateFocus = swiper => {
    swiper.slides.forEach(slide => {
      const link = slide.querySelector('a');
      if (link) link.setAttribute('tabIndex', '-1');
    });

    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;

    const activeLink = activeSlide.querySelector('a');
    if (activeLink) activeLink.setAttribute('tabIndex', '0');
  };

  // 활성 pagination 버튼에 aria-current 반영
  const updateBulletAria = swiper => {
    const bullets = swiper.pagination?.bullets;
    if (!bullets?.length) return;
    bullets.forEach((bullet, i) => {
      bullet.setAttribute('aria-current', i === swiper.realIndex ? 'true' : 'false');
    });
  };

  const toggleAutoplay = () => {
    const newPlaying = !isPlayingRef.current;
    isPlayingRef.current = newPlaying;
    setIsPlaying(newPlaying);
    if (newPlaying) {
      swiperRef.current?.autoplay?.start();
    } else {
      swiperRef.current?.autoplay?.stop();
    }
  };

  const handleSwiperInit = swiper => {
    swiperRef.current = swiper;

    const container = swiper.el;

    // 키보드 접근성 — 사용자가 명시적으로 정지한 경우 재개하지 않음
    container.addEventListener('focusin', () => {
      if (isPlayingRef.current) swiperRef.current?.autoplay?.stop();
    });

    container.addEventListener('focusout', () => {
      if (isPlayingRef.current) swiperRef.current?.autoplay?.start();
    });

    // 마우스 hover 접근성 — 사용자가 명시적으로 정지한 경우 재개하지 않음
    container.addEventListener('mouseenter', () => {
      if (isPlayingRef.current) swiperRef.current?.autoplay?.stop();
    });

    container.addEventListener('mouseleave', () => {
      if (isPlayingRef.current) swiperRef.current?.autoplay?.start();
    });
  };

  const handleInit = swiper => {
    updateFocus(swiper);
    updateBulletAria(swiper);
  };

  const handleSlideChange = swiper => {
    updateFocus(swiper);
    updateBulletAria(swiper);
  };

  return (
    <Box className="banner-slider">
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{
          clickable: true,
          renderBullet: (index, className) => {
            return `<button class="${className}" name="${index + 1}번째 슬라이드" aria-label="${index + 1}번째 슬라이드" aria-current="false"><span class="visually-hidden">${index + 1}번째 슬라이드</span></button>`;
          }
        }}
        loop
        slidesPerView={1}
        onSwiper={handleSwiperInit}
        onInit={handleInit}
        onSlideChangeTransitionEnd={handleSlideChange}
      >
        {banners.map(banner => (
          <SwiperSlide key={banner.id}>
            <Link to={banner.to} title={banner.title} className="slim-banner">
              <img src={banner.src} alt={banner.alt} />
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
      <button
        type="button"
        className={`banner-pause-btn${isPlaying ? '' : ' is-paused'}`}
        onClick={toggleAutoplay}
        aria-label={isPlaying ? '자동 재생 정지' : '자동 재생 시작'}
        aria-pressed={!isPlaying}
      >
        <span className="visually-hidden">{isPlaying ? '자동 재생 정지' : '자동 재생 시작'}</span>
      </button>
    </Box>
  );
};

export default BannerSlider;
